/**
 * Where the "confirm your new address" link lands: /settings/email/:token
 *
 * The backend emails `${APP_URL}/settings/email/${token}` to the NEW
 * address, so the shape is fixed. The link is redeemed exactly once (the
 * ref guard, as on the sign-in callback: StrictMode runs effects twice and
 * the token is single-use). On success the backend has already ended every
 * session, so the page signs this browser out too and points at sign-in
 * with the new address filled in. Being signed out is fine here: the
 * request came from a signed-in session and the link proves the mailbox.
 */
import { useEffect, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { signOut as fbSignOut } from 'firebase/auth';
import { auth } from '../../lib/firebase';
import { isCallFailure } from '../../lib/callable';
import { Spinner } from '../../app/Spinner';
import { confirmEmailChange } from './useEmailChange';
import styles from '../../auth/SignIn.module.css';
import { Wordmark } from '../../shared/Wordmark';

export function EmailChangeConfirmPage() {
  const { token } = useParams<{ token: string }>();
  const [email, setEmail] = useState<string | null>(null);
  const [failed, setError] = useState<string | null>(null);
  const attempted = useRef(false);

  // Derived, not stored: there is nothing to try when the link has no token.
  const incomplete = !token ? 'This link is incomplete. Open it from the email again.' : null;

  const error = failed ?? incomplete;

  useEffect(() => {
    if (attempted.current || !token) return;
    attempted.current = true;
    (async () => {
      try {
        const r = await confirmEmailChange(token);
        setEmail(r.email);
        // The backend revoked every session; this browser's would only fail at its next refresh.
        try { await fbSignOut(auth); } catch { /* already signed out */ }
      } catch (e) {
        setError(isCallFailure(e) ? e.message : 'The change could not be confirmed. Ask for a new link from Settings.');
      }
    })();
  }, [token]);

  return (
    <main className={styles.page}>
      <div className={styles.card}>
        <div className={styles.brand}>
          <Wordmark withIcon className={styles.brandMark} />
        </div>
        {!email && !error && (
          <>
            <h1 className={styles.title}>Confirming your new address</h1>
            <Spinner label="Confirming your new address" />
          </>
        )}
        {email && (
          <>
            <h1 className={styles.title}>Your address is now {email}</h1>
            <p className={styles.lede}>
              Sign-in links come to this address from now on. You have been signed out everywhere, so sign in again with it.
            </p>
            <Link to={`/signin?email=${encodeURIComponent(email)}`} className={styles.submit} style={{ display: 'block', textAlign: 'center', textDecoration: 'none' }}>
              Sign in with {email}
            </Link>
          </>
        )}
        {error && (
          <>
            <h1 className={styles.title}>This link did not work</h1>
            <div className={styles.error} role="alert">{error}</div>
            <p className={styles.lede}>Your address has not changed. Sign in with your current address and ask for a new link from Settings.</p>
            <Link to="/settings" className={styles.submit} style={{ display: 'block', textAlign: 'center', textDecoration: 'none' }}>
              Go to Settings
            </Link>
          </>
        )}
      </div>
    </main>
  );
}
